/**
 * Function to mimic Excel formula for calculating risk class
 * likelihood: "R" | "U" | "P" | "L" | "AC"
 * delta: Delta between financial impact and consequence threshold
 */
export function evalRiskClass(likelihood: string, delta: number): string {
  switch (likelihood) {
    case 'R': {
      if (delta < 0) return 'I'
      if (delta === 0) return 'II'
      if (delta > 0) return 'III'
      return ''
    }
    case 'U': {
      if (delta < 0) return 'I'
      if (delta === 0) return 'II'
      if (delta === 1) return 'III'
      if (delta > 1) return 'IV'
      return ''
    }
    case 'P': {
      if (delta < -1) return 'I'
      if (delta === -1) return 'II'
      if (delta === 0) return 'III'
      if (delta > 0) return 'IV'
      return ''
    }
    case 'L': {
      if (delta < -1) return 'II'
      if (delta === -1 || delta === 0) return 'III'
      if (delta > 0) return 'IV'
      return ''
    }
    case 'AC': {
      if (delta < -1) return 'II'
      if (delta === -1 || delta === 0) return 'III'
      if (delta > -1) return 'IV'
      return ''
    }
    default:
      return ''
  }
}

/**
 * Function to get consequence severity based on consequence score
 * @param delta - The delta between consequence number and threshold
 * @returns Severity level string
 */
export function getConsequenceSeverity(delta: number): string {
  if (delta <= -2) return 'Very Low'
  if (delta === -1) return 'Low'
  if (delta === 0) return 'Moderate'
  if (delta === 1) return 'High'
  if (delta >= 2) return 'Very High'
  return 'Unknown'
}

/**
 * Converts financial impact to consequence number (1-18)
 * @param financialImpact - Dollar amount for financial impact
 * @returns Consequence number from 1 to 18
 */
export function getConsequenceNumber(financialImpact: number): number {
  if (financialImpact < 50000) {
    return 1
  } else if (financialImpact < 100000) {
    return 2
  } else if (financialImpact < 250000) {
    return 3
  } else if (financialImpact < 500000) {
    return 4
  } else if (financialImpact < 1000000) {
    return 5
  } else if (financialImpact < 2500000) {
    return 6
  } else if (financialImpact < 5000000) {
    return 7
  } else if (financialImpact < 10000000) {
    return 8
  } else if (financialImpact < 25000000) {
    return 9
  } else if (financialImpact < 50000000) {
    return 10
  } else if (financialImpact < 100000000) {
    return 11
  } else if (financialImpact < 250000000) {
    return 12
  } else if (financialImpact < 500000000) {
    return 13
  } else if (financialImpact < 1000000000) {
    return 14
  } else if (financialImpact < 2500000000) {
    return 15
  } else if (financialImpact < 5000000000) {
    return 16
  } else if (financialImpact < 10000000000) {
    return 17
  } else {
    return 18
  }
}

/**
 * Function to calculate risk class based on likelihood and financial impact
 * @param currentLikelihood - Number from 1 to 5 (1=Rare, 2=Unlikely, 3=Possible, 4=Likely, 5=Almost Certain)
 * @param financialImpact - Dollar amount for financial impact
 * @param consequenceThreshold - Default 10; sets the threshold for the consequence rating used in risk assessment
 * @returns Object containing risk class and consequence severity
 */
export async function getRiskClass(
  currentLikelihood: number,
  financialImpact: number,
  consequenceThreshold: number
): Promise<{
  riskClass: string
  consequenceSeverity: string
}> {
  const consequenceNumber = getConsequenceNumber(financialImpact)

  // Convert number to text first
  const likelihoodText = currentLikelihoodToText(currentLikelihood)

  if (!likelihoodText) {
    throw new Error('Current likelihood must be a number from 1 to 5')
  }

  const likelihoodMap: { [key: string]: string } = {
    Rare: 'R',
    Unlikely: 'U',
    Possible: 'P',
    Likely: 'L',
    'Almost Certain': 'AC',
  }

  const likelihoodLetter = likelihoodMap[likelihoodText]

  const delta = consequenceNumber - consequenceThreshold
  const riskClass = evalRiskClass(likelihoodLetter, delta)
  const consequenceSeverity = getConsequenceSeverity(delta)

  return {
    riskClass,
    consequenceSeverity,
  }
}

/**
 * Converts current_likelihood number (1-5) to text
 * @param currentLikelihood - Number from 1 to 5
 * @returns Likelihood text
 */
export const LIKELIHOOD_NUMBER_TO_TEXT: { [key: number]: string } = {
  1: 'Rare',
  2: 'Unlikely',
  3: 'Possible',
  4: 'Likely',
  5: 'Almost Certain',
}

const LIKELIHOOD_TEXT_TO_NUMBER: { [key: string]: number } = {
  rare: 1,
  unlikely: 2,
  possible: 3,
  likely: 4,
  'almost certain': 5,
}

const normalize = (value?: string | null): string | undefined => {
  if (!value) return undefined
  const normalized = value.trim().toLowerCase()
  return normalized.length > 0 ? normalized : undefined
}

export function currentLikelihoodToText(currentLikelihood: number): string {
  return LIKELIHOOD_NUMBER_TO_TEXT[currentLikelihood] || ''
}

export interface LikelihoodMappingConfig {
  likelihoodLabels?: Array<{
    value?: string | null
    description?: string | null
  }>
  likelihoodCriteria?: Array<{
    description?: string | null
    frequency?: string | null
    probability?: string | null
  }>
}

/**
 * Converts likelihood text to current_likelihood number (1-5)
 * @param likelihoodText - Text: 'Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'
 * @returns Number from 1 to 5
 */
export function textToCurrentLikelihood(
  likelihoodText: string,
  mapping?: LikelihoodMappingConfig | null
): number {
  const normalized = normalize(likelihoodText)
  if (!normalized) return 0

  const labels = mapping?.likelihoodLabels
  if (labels && labels.length > 0) {
    const labelIndex = labels.findIndex((label) => {
      const description = normalize(label.description)
      const value = normalize(label.value)
      return description === normalized || value === normalized
    })
    if (labelIndex >= 0) return labelIndex + 1
  }

  const criteria = mapping?.likelihoodCriteria
  if (criteria && criteria.length > 0) {
    const criteriaIndex = criteria.findIndex((item) => {
      const description = normalize(item.description)
      const frequency = normalize(item.frequency)
      const probability = normalize(item.probability)
      return (
        description === normalized ||
        frequency === normalized ||
        probability === normalized
      )
    })
    if (criteriaIndex >= 0) return criteriaIndex + 1
  }

  return LIKELIHOOD_TEXT_TO_NUMBER[normalized] || 0
}

/**
 * Business value options for consequence threshold mapping
 */
export type BusinessValueOption =
  | '<$2M'
  | '$4M'
  | '$10M'
  | '$20M'
  | '$40M'
  | '$100M'
  | '$200M'
  | '$400M'
  | '$1B'
  | '$2B'
  | '$4B'

/**
 * Maps business value options to consequence thresholds
 * @param businessValue - The selected business value option
 * @returns Consequence threshold number
 */
export function getConsequenceThresholdFromBusinessValue(
  businessValue: BusinessValueOption
): number {
  const businessValueToThreshold: { [key in BusinessValueOption]: number } = {
    '<$2M': 3,
    $4M: 4,
    $10M: 5,
    $20M: 6,
    $40M: 7,
    $100M: 8,
    $200M: 9,
    $400M: 10, // Default
    $1B: 11,
    $2B: 12,
    $4B: 13,
  }

  return businessValueToThreshold[businessValue]
}

/**
 * Gets all available business value options
 * @returns Array of business value options
 */
export function getBusinessValueOptions(): BusinessValueOption[] {
  return [
    '<$2M',
    '$4M',
    '$10M',
    '$20M',
    '$40M',
    '$100M',
    '$200M',
    '$400M',
    '$1B',
    '$2B',
    '$4B',
  ]
}

/**
 * Default business value option ($400M)
 */
export const DEFAULT_BUSINESS_VALUE: BusinessValueOption = '$400M'

/**
 * Calculate risk and consequence using the default method
 * @param currentLikelihood - Number from 1 to 5 (1=Rare, 2=Unlikely, 3=Possible, 4=Likely, 5=Almost Certain)
 * @param financialImpact - Dollar amount for financial impact
 * @param consequenceThreshold - Default 10; sets the threshold for the consequence rating used in risk assessment
 * @returns Object containing risk class and consequence position
 */
export async function calculateDefaultRiskAndConsequence(
  currentLikelihood: number,
  financialImpact: number,
  consequenceThreshold: number
): Promise<{
  riskClass: string
  consequencePosition: number | null
}> {
  const { riskClass, consequenceSeverity } = await getRiskClass(
    currentLikelihood,
    financialImpact,
    consequenceThreshold
  )
  const consequenceSeverityToPosition: Record<string, number> = {
    'Very Low': 1,
    Low: 2,
    Moderate: 3,
    High: 4,
    'Very High': 5,
  }

  const consequencePosition =
    consequenceSeverityToPosition[consequenceSeverity] || null

  return {
    riskClass,
    consequencePosition,
  }
}

/**
 * Calculate risk and consequence using the HFS method
 * @param currentLikelihood
 * @param financialImpact - Dollar amount for financial impact
 * @param riskMatrix - The risk matrix from config
 * @param riskMatrixOrientation - The matrix orientation from config
 * @returns Object containing risk class and consequence position
 */
export async function calculateHfsRiskAndConsequence(
  currentLikelihood: number,
  financialImpact: number,
  riskMatrix?: string[][],
  riskMatrixOrientation?: string
): Promise<{
  riskClass: string
  consequencePosition: number
}> {
  let consequencePosition: number

  if (financialImpact < 1000) {
    consequencePosition = 7
  } else if (financialImpact < 10000) {
    consequencePosition = 6
  } else if (financialImpact < 100000) {
    consequencePosition = 5
  } else if (financialImpact < 1000000) {
    consequencePosition = 4
  } else if (financialImpact < 10000000) {
    consequencePosition = 3
  } else if (financialImpact < 100000000) {
    consequencePosition = 2
  } else {
    consequencePosition = 1
  }

  if (!riskMatrix || riskMatrix.length === 0) {
    return {
      riskClass: '--',
      consequencePosition,
    }
  }

  // /utils can't import from types so not using the enum
  const isLikelihoodAsRows = riskMatrixOrientation === 'likelihood_as_rows'

  // converting to 0-based here
  const row = isLikelihoodAsRows
    ? currentLikelihood - 1
    : consequencePosition - 1
  const col = isLikelihoodAsRows
    ? consequencePosition - 1
    : currentLikelihood - 1

  if (
    row >= 0 &&
    row < riskMatrix.length &&
    col >= 0 &&
    col < riskMatrix[row].length
  ) {
    const riskClass = riskMatrix[row][col]
    return {
      riskClass,
      consequencePosition,
    }
  } else {
    // Fallback if indices are out of bounds
    return {
      riskClass: '--',
      consequencePosition,
    }
  }
}

export interface RiskCalculationVariables {
  consequenceThreshold?: number
  riskMatrix?: string[][]
  riskMatrixOrientation?: string
}

// Risk calculation method redefined here to avoid the circ import=
export const RiskCalculationMethod = {
  DEFAULT: 'default',
  HFS_METHOD: 'hfs_method',
}

export type RiskCalculationMethodType =
  (typeof RiskCalculationMethod)[keyof typeof RiskCalculationMethod]

/**
 * Unified function to calculate risk and consequence using different methods
 * @param method - The calculation method to use
 * @param likelihood - Defined as a Number
 * @param financialImpact - Dollar amount for financial impact
 * @param variables - Method-specific variables
 * @returns Object containing risk class and consequence position
 */
export async function calculateRiskAndConsequence(
  method: RiskCalculationMethodType = RiskCalculationMethod.DEFAULT,
  likelihood: number,
  financialImpact: number,
  variables: RiskCalculationVariables = {}
): Promise<{
  riskClass: string
  consequencePosition: number | null
}> {
  switch (method) {
    case RiskCalculationMethod.HFS_METHOD:
      return await calculateHfsRiskAndConsequence(
        likelihood,
        financialImpact,
        variables.riskMatrix,
        variables.riskMatrixOrientation
      )
    default:
      return await calculateDefaultRiskAndConsequence(
        likelihood,
        financialImpact,
        variables.consequenceThreshold || 10
      )
  }
}
